import { Injectable } from '@angular/core';
import {
  GetObjectCommand,
  GetObjectCommandOutput,
  ListObjectsV2Command,
  PutObjectCommand,
  S3Client,
} from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { AwsCredentialIdentity } from '@aws-sdk/types';
import { environment } from '../environments/environment';
import { assertValidAwsRegion } from './aws-region';

@Injectable({
  providedIn: 'root',
})
export class S3Service {
  private s3Client: S3Client;

  constructor() {
    this.s3Client = new S3Client({
      region: assertValidAwsRegion(environment.iot.region),
      // Resolve credentials at request time so refreshed Cognito keys are used.
      credentials: async () => this.getSessionCredentials(),
    });
  }

  private getSessionCredentials(): AwsCredentialIdentity {
    const accessKeyId = sessionStorage.getItem('accessKeyId') || '';
    const secretAccessKey = sessionStorage.getItem('secretAccessKey') || '';
    const sessionToken = sessionStorage.getItem('sessionToken') || '';
    if (!accessKeyId || !secretAccessKey) {
      throw new Error('Missing AWS credentials in session storage.');
    }
    return { accessKeyId, secretAccessKey, sessionToken };
  }

  // Upload a generated Zip Folder of newly provisioned Thing Certificates to an S3 bucket
  async uploadCertificateFolderToS3(
    folderName: string,
    zipContent: any,
    zipFolderName: string
  ) {
    // Upload the zipped folder to the specified bucket and folder
    await this.s3Client.send(
      new PutObjectCommand({
        Bucket: environment.s3.bucketName,
        Key: environment.s3.folderPath + `${folderName}/${zipFolderName}`,
        Body: zipContent,
        ContentType: 'application/zip',
      })
    );
  }

  async getCertificatesForThing(thingName: string) {
    const params = {
      Bucket: environment.s3.bucketName,
      Prefix: environment.s3.folderPath + thingName,
    };

    try {
      const data = await this.s3Client.send(new ListObjectsV2Command(params));
      return (
        await Promise.all(
          (data.Contents || []).map((object: any) =>
            this.generatePresignedUrl(object.Key)
          )
        )
      ).filter(Boolean) as string[];
    } catch (error) {
      console.error('Error listing objects:', error);
      return [];
    }
  }

  private async generatePresignedUrl(objectKey: string = ''): Promise<string> {
    const params = {
      Bucket: environment.s3.bucketName,
      Key: objectKey,
    };

    return getSignedUrl(
      this.s3Client,
      new GetObjectCommand(params),
      { expiresIn: 60 } // seconds
    );
  }

  async uploadFileToS3(file: File, key: string, contentType: string){
   try{
    await this.s3Client.send(new PutObjectCommand({
      Bucket: environment.s3.chatBucket,
      Key: key,
      Body: file,
      ContentType: contentType,
      ACL: 'public-read',
    }));
   } catch (error) {
    console.error('Error uploading file to s3:', error);
   }
  }

public async  getFileFromS3(fileKey: string): Promise<GetObjectCommandOutput | null>{
   try {
   const s3File = await this.s3Client.send(new GetObjectCommand({
     Bucket: environment.s3.chatBucket,
     Key: fileKey,
   }));

    return s3File
   } catch (error) {
    console.error('File fetch error:', error);
    return null
   }
  }
}
